import _styled from "@emotion/styled-base";
import { useState, useEffect, createElement, useCallback } from 'react';
import '@reach/slider/styles.css';
import { usePopper } from 'react-popper';
import { withTheme } from 'emotion-theming';
import { rem } from 'polished';
import { variant } from 'styled-system';
import { SliderHandle, SliderTrackHighlight, SliderTrack, SliderMarker, SliderInput } from '@reach/slider';
import shouldForwardProp from '@styled-system/should-forward-prop';

const TooltipArrow = _styled("div", {
  target: "e72qbvm0",
  label: "TooltipArrow"
})("background:#ffffff;[data-popper-placement^='bottom'] > &{top:", rem('-5px'), ";::after{border-left:1px solid #000000;border-top:1px solid #000000;}}[data-popper-placement^='top'] > &{bottom ", rem('-5px'), ";::after{border-right:1px solid #000000;border-bottom:1px solid #000000;}}[data-popper-placement^='right'] > &{left ", rem('-5px'), ";::after{border-left:1px solid #000000;border-bottom:1px solid #000000;}}[data-popper-placement^='left'] > &{right ", rem('-5px'), ";::after{border-right:1px solid #000000;border-top:1px solid #000000;}}", variant({
  scale: 'rangeInput.tooltip.arrow'
}), " width:", rem('8px'), ";height:", rem('8px'), ";z-index:-1;::after{content:'';transform:rotate(45deg);width:", rem('7px'), ";height:", rem('7px'), ";z-index:-1;position:absolute;}");

const TooltipContainer = withTheme(_styled("div", {
  target: "e72qbvm1",
  label: "TooltipContainer"
})("border:1px solid #000000;background-color:#ffffff;padding:", rem('7px'), ";font-size:", rem('12px'), ";", variant({
  scale: 'rangeInput.tooltip.container'
})));

const Tooltip = props => {
  const [popperElement, setPopperElement] = useState(null);
  const [arrowElement, setArrowElement] = useState(null);
  const {
    text,
    variant
  } = props;
  const {
    styles,
    attributes,
    forceUpdate
  } = usePopper(props.referenceElement, popperElement, {
    placement: 'bottom',
    modifiers: [{
      name: 'offset',
      options: {
        offset: [0, (popperElement === null || popperElement === void 0 ? void 0 : popperElement.offsetHeight) / 4]
      }
    }, {
      name: 'arrow',
      options: {
        element: arrowElement
      }
    }, {
      name: 'preventOverflow',
      options: {
        altAxis: true
      }
    }]
  });
  useEffect(() => {
    if (forceUpdate) {
      forceUpdate();
    }
  }, [forceUpdate, text]);
  return createElement(TooltipContainer, Object.assign({
    ref: setPopperElement,
    style: styles.popper
  }, attributes.popper, {
    variant: variant
  }), props.text, createElement(TooltipArrow, {
    ref: setArrowElement,
    style: styles.arrow,
    variant: variant
  }));
};

const StyledSliderHandle = withTheme( /*#__PURE__*/_styled(SliderHandle, {
  shouldForwardProp,
  target: "e72qbvm2",
  label: "StyledSliderHandle"
})(variant({
  scale: 'rangeInput.sliderHandle'
}), " &:focus{outline:none;cursor:pointer;}&:hover{cursor:pointer;}width:", rem('14px'), " !important;height:", rem('14px'), " !important;"));
const StyledSliderTrackHighlight = withTheme( /*#__PURE__*/_styled(SliderTrackHighlight, {
  shouldForwardProp,
  target: "e72qbvm3",
  label: "StyledSliderTrackHighlight"
})(variant({
  scale: 'rangeInput.sliderTrackHighlight'
})));
const StyledSliderTrack = withTheme( /*#__PURE__*/_styled(SliderTrack, {
  shouldForwardProp,
  target: "e72qbvm4",
  label: "StyledSliderTrack"
})(variant({
  scale: 'rangeInput.sliderTrack'
}), " border-radius:", rem('4px'), " !important;"));
const StyledSliderMarker = withTheme( /*#__PURE__*/_styled(SliderMarker, {
  shouldForwardProp,
  target: "e72qbvm5",
  label: "StyledSliderMarker"
})("border:none !important;background:#000000 !important;&[data-state='under-value']{background:#ffffff !important;}", variant({
  scale: 'rangeInput.sliderMarker'
}), " width:", rem('2px'), ";height:", rem('2px'), ";"));

const StyledSliderInput = /*#__PURE__*/_styled(SliderInput, {
  shouldForwardProp,
  target: "e72qbvm6",
  label: "StyledSliderInput"
})("height:", rem('6px'), " !important;cursor:", props => props.isActive ? 'pointer' : 'auto', ";");

const getScale = value => Math.floor(value) === value ? 1 : Math.pow(10, value.toString().split('.')[1].length);

const RangeInput = props => {
  const [referenceElement, setReferenceElement] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const {
    max,
    min,
    step,
    variant,
    value,
    getAriaValueText
  } = props;
  const scale = getScale(step);
  const [scaledMax, scaledMin, scaledStep] = [scale * max, scale * min, scale * step];
  const markers = [];

  for (let i = scaledMin + scaledStep; i < scaledMax; i = i + scaledStep) {
    markers.push(createElement(StyledSliderMarker, {
      value: i / scale,
      key: i,
      variant: variant
    }));
  }

  const setSliderActive = useCallback(() => {
    setIsActive(true);
  }, []);
  const setSliderInactive = useCallback(() => {
    setIsActive(false);
  }, []);
  return createElement(StyledSliderInput, Object.assign({}, props, {
    onPointerDown: setSliderActive,
    onPointerUp: setSliderInactive,
    onFocus: setSliderActive,
    onBlur: setSliderInactive,
    isActive: isActive
  }), createElement(StyledSliderTrack, {
    variant: variant
  }, createElement(StyledSliderTrackHighlight, {
    variant: variant
  }), createElement(StyledSliderHandle, {
    variant: variant,
    ref: setReferenceElement
  }), isActive && createElement(Tooltip, {
    referenceElement: referenceElement,
    text: getAriaValueText(value),
    variant: variant
  }), markers));
};

export { RangeInput };
//# sourceMappingURL=index.js.map
